import { useFormik } from "formik";
import React, { useEffect, useState, useCallback } from "react";
import { useToast } from "../../contexts/ToastContext";
import { GetNotes, addNote, addNoteDTO, notesGridDTO, deleteNote, toggleCompletedNote, getNotesWithPagination, getNoteTypesForFilterDropdown, getNotePriorities, NOTE_PRIORITY_NORMAL } from "../../data/notes";
import { Grid, GridColumn, GridSortChangeEvent, GridFilterChangeEvent, GridPageChangeEvent } from '@progress/kendo-react-grid';
import { SortDescriptor, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Switch } from "@progress/kendo-react-inputs";
import { FaTimesCircle } from "react-icons/fa";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import dayjs from "dayjs";

import '../../components/FormComps/FormikStyles.css'
import "../../components/grids/Grids.css";
import { dropDown } from "../../data/interfaces";
import { FormMultiSelect, FormSwitch, FormDatePicker, FormDropDownList } from "../../components/FormComps/FormComponents";
import { getEmployeeDropdown } from "../../data/employeeData";

const Notes = (props: any) => {
  const sourceEntity: addNoteDTO = props.sourceEntity;
  const [refreshGrid, setRefreshGrid] = useState<boolean>(false);

  const onToggleRefresh = () => {
      setRefreshGrid(!refreshGrid);
  }

  console.log(props.lockEdit)

  return (
    <div>
        {
          props.lockEdit === false ? ( 
            <div>
                <AddNote entitySource={sourceEntity} onNoteSubmit={onToggleRefresh}/>
            </div>
          ) : null
        }
        <div>
            <NotesGrid lockEdit={props.lockEdit} entitySource={sourceEntity} onRefreshRequired={refreshGrid} onDataBound={onToggleRefresh} />
        </div>
    </div>        
  );
}

// Custom filter cell for type column with dropdown
const NoteTypeFilterCell = (props: any) => {
  const [filteredData, setFilteredData] = useState<any[]>(props.data || []);
  const [loading, setLoading] = useState(false);
  
  const hasValue = (value: any) => Boolean(value && value !== '');
  
  const onChange = (event: any) => {
    const value = event.target.value;
    props.onChange({
      value: hasValue(value) ? value : '',
      operator: hasValue(value) ? 'contains' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const onFilterChange = async (event: any) => {
    const filter = event.filter.value;
    
    try {
      setLoading(true);
      const typesData = await getNoteTypesForFilterDropdown(filter);
      const typeNames = typesData.map((t: any) => t.text).sort();
      setFilteredData(typeNames);
    } catch (error) {
      console.error('Error loading filtered note types:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFilteredData(props.data || []);
  }, [props.data]);

  return (
    <ComboBox
      data={filteredData}
      onChange={onChange}
      onFilterChange={onFilterChange}
      value={props.value || ''}
      placeholder="Filter by type..."
      filterable={true}
      allowCustom={true}
      loading={loading}
      className="k-filtercell-dropdown"
    />
  );
};

// Custom filter cell for details column with dropdown
const NoteDetailsFilterCell = (props: any) => {
  const [filteredData, setFilteredData] = useState<any[]>(props.data || []);
  const [loading, setLoading] = useState(false);
  
  const hasValue = (value: any) => Boolean(value && value !== '');
  
  const onChange = (event: any) => {
    const value = event.target.value;
    props.onChange({
      value: hasValue(value) ? value : '',
      operator: hasValue(value) ? 'contains' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const onFilterChange = async (event: any) => {
    const filter = event.filter.value;
    
    try {
      setLoading(true);
      // Get all notes and extract unique details
      setFilteredData(props.data || []);
    } catch (error) {
      console.error('Error loading filtered note details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFilteredData(props.data || []);
  }, [props.data]);

  return (
    <ComboBox
      data={filteredData}
      onChange={onChange}
      onFilterChange={onFilterChange}
      value={props.value || ''}
      placeholder="Filter by note..."
      filterable={true}
      allowCustom={true}
      loading={loading}
      className="k-filtercell-dropdown"
    />
  );
};

const NotesGrid = (props: any) => {
    const [filteredData, setFilteredData] = useState<notesGridDTO[]>([]);
    const [skip, setSkip] = useState(0);
    const [take, setTake] = useState(20);
    const [sort, setSort] = useState<SortDescriptor[]>([]);
    const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);
    const [total, setTotal] = useState(0);
    const [refreshGrid, setRefreshGrid] = useState<boolean>(false);

    const handleDeleteNote = async (noteId: number) => {
      await deleteNote(noteId).then(() => {
        setRefreshGrid(!refreshGrid);
      });
    }

    const handleNoteCompleteToggle = async (noteId: number) => {
      await toggleCompletedNote(noteId).then(() => {
        setRefreshGrid(!refreshGrid);
      });
    }

    const loadData = useCallback(async () => {
      try {
        const result = await getNotesWithPagination({
          sourceEntity: props.entitySource,
          skip,
          take,
          sort,
          filter,
          showCompleted: false,
        });
        setFilteredData(result.data);
        setTotal(result.total);
      } catch (error) {
        console.error('Error loading notes:', error);
      }
    }, [props.entitySource, skip, take, sort, filter]);

    useEffect(() => {
      loadData();
    }, [loadData, refreshGrid]);

    useEffect(() => {
      if (props.onRefreshRequired) {
        setRefreshGrid(!refreshGrid);
        props.onDataBound();
      }
    }, [props.onRefreshRequired, props.onDataBound]);

    const handleSortChange = (event: GridSortChangeEvent) => {
      setSort(event.sort);
      setSkip(0);
    };

    const handleFilterChange = (event: GridFilterChangeEvent) => {
      setFilter(event.filter);
      setSkip(0);
    };

    const handlePageChange = (event: GridPageChangeEvent) => {
      setSkip(event.page.skip);
      setTake(event.page.take);
    };

    const rowRender = (trElement: any, rowProps: any) => {
      const dataItem = rowProps.dataItem as notesGridDTO;
      let bgColor = "rgb(245 249 251)";
      
      if (dataItem.isCompleted) {
        bgColor = "#d7edd7";
      }

      const newTrElement = React.cloneElement(
        trElement,
        { style: { backgroundColor: bgColor, border: "1px solid rgb(216 229 237)" } },
        trElement.props.children
      );

      return newTrElement;
    };

    return (
        <div className="p-2">
          <div className="h-2" />
          <Grid
            data={filteredData}
            total={total}
            skip={skip}
            take={take}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}
            onFilterChange={handleFilterChange}
            sort={sort}
            filter={filter}
            sortable={true}
            pageable={{ pageSizes: [10, 20, 30, 40, 50] }}
            rowRender={rowRender}
          >
            <GridColumn
              field="assignedTo"
              title="Assigned To"
              width="200px"
              filterable={false}
              cell={(cellProps: any) => (
                <td>
                  <span>{cellProps.dataItem.assignedEmployees?.map((e: any) => e.employeeName).join(", ") || ""}</span>
                </td>
              )}
            />
            <GridColumn
              field="noteType"
              title="Type"
              width="120px"
              filterCell={NoteTypeFilterCell}
              cell={(cellProps: any) => (
                <td>
                  <div className="priority-cell" style={{ width: "80px" }}>
                    <div className={`notetype__${cellProps.dataItem.noteType?.toLowerCase()}`}>
                      {cellProps.dataItem.noteType}
                    </div>
                  </div>
                </td>
              )}
            />
            <GridColumn
              field="note"
              title="Details"
              width="400px"
              filterCell={NoteDetailsFilterCell}
              cell={(cellProps: any) => (
                <td title={`${cellProps.dataItem.createdDate} - ${cellProps.dataItem.createdBy}`}>
                  <span>{cellProps.dataItem.note}</span>
                </td>
              )}
            />
            <GridColumn
              field="createdDate"
              title="Created Date"
              width="140px"
              filterable={false}
              cell={(cellProps: any) => (
                <td>
                  <span>{dayjs(cellProps.dataItem.createdDate).format("DD/MM/YYYY")}</span>
                </td>
              )}
            />
            <GridColumn
              field="createdBy"
              title="Created By"
              width="150px"
              filterable={false}
            />
            <GridColumn
              field="dueDate"
              title="Due Date"
              width="120px"
              filterable={false}
              cell={(cellProps: any) => (
                <td>
                  <span>{cellProps.dataItem.dueDate ? dayjs(cellProps.dataItem.dueDate).format("DD/MM/YYYY") : ""}</span>
                </td>
              )}
            />
            <GridColumn
              field="completed"
              title="Done"
              width="80px"
              filterable={false}
              sortable={false}
              cell={(cellProps: any) => {
                if (props.lockEdit === false) {
                  return (
                    <td>
                      <Switch 
                        checked={cellProps.dataItem.isCompleted === true} 
                        onChange={() => handleNoteCompleteToggle(cellProps.dataItem.noteId)}
                        size="small"
                      />
                    </td>
                  );
                } else {
                  return (
                    <td>
                      <input 
                        type="checkbox" 
                        checked={cellProps.dataItem.isCompleted === true}
                        readOnly
                        style={{ cursor: "pointer" }}
                      />
                    </td>
                  );
                }
              }}
            />
          </Grid>
        </div>
      );
}

const AddNote = (props: {entitySource: addNoteDTO, onNoteSubmit: any}) => {
    const { showToast } = useToast();
    const [initialValues, setInitialValues] = useState({
        note: "",
        employeesAssignedToId: 0,
        dueDate: null as Date | null,
        notePriorityId: NOTE_PRIORITY_NORMAL,
    });

    const [employeeData, setEmployeeData] = useState<dropDown[]>([])
    const [priorityData, setPriorityData] = useState<dropDown[]>([])

    // const formik = useFormik({
    //     initialValues: initialValues,
    //     enableReinitialize: true,
    //     onSubmit: async (values: any, {resetForm}) => {            
    //         await submitNote(values);
    //         resetForm();
    //     },
    // });


    const submitNote = async (values: any) => {
      console.log(values);
      await addNote(
        props.entitySource,
        values.note,
        values.employeesAssignedToId.map((emp: { value: number; text: string }) => emp.value),
        values.dueDate,
        values.notePriorityId
      )
        .then(() => {
          props.onNoteSubmit();
          setInitialValues({
            note: "",
            employeesAssignedToId: 0,
            dueDate: null,
            notePriorityId: NOTE_PRIORITY_NORMAL,
          });
        })
        .then(() => {
          showToast("Note succesfully added", "success");
        })
        .catch((error) => {
          showToast("An error occurred creating a new note", "error");
          console.log(error);
        });
    };    

    const searchEmployeeDropdown = (searchTerm: string) => {
      const fetchEmployeeData = async (search: string) => {
        await getEmployeeDropdown(search).then((result) => {
          setEmployeeData(result)
        })
      }
  
      fetchEmployeeData(searchTerm)
    }

    const handleSubmit = async (e:any) => {
      e.preventDefault()
      await submitNote(initialValues);

    }

    const fetchEmployeeData = async () => {
      const employeeData = await getEmployeeDropdown('')
      setEmployeeData(employeeData)
    }

    const fetchPriorityData = async () => {
      const priorities = await getNotePriorities()
      setPriorityData(priorities.map((p) => ({ text: p.name, value: p.notePriorityId })))
    }

    useEffect(() => {
      fetchEmployeeData()
      fetchPriorityData()
    }, []);

    console.log(initialValues)

    return (        
        <div style={{padding: "1em"}}>
                  
          <form onSubmit={(e) => handleSubmit(e)}>  
            <FormMultiSelect 
              required={true}
              label={'Assigned employees:'}
              searchDropdown={searchEmployeeDropdown}
              data={employeeData}     
              setData={setEmployeeData}
              value={initialValues.employeesAssignedToId}
              onChange={(e: any) => {
                setInitialValues((prev) => ({
                  ...prev,
                  employeesAssignedToId: e.value,
                }));
              }}

            />

            {/* <FormDropDownListFilter 
              multiple={true}
              data={employeeData}
              name={'employeesAssignedToId'}
              label={'Assigned employee:'}
              setData={setEmployeeData}
              placeholder={'Choose a employee to assign:'}
              searchDropdown={searchEmployeeDropdown}
              onChange={(e: any) => {
                setInitialValues((prev) => ({
                  ...prev,
                  employeesAssignedToId: parseInt(e.value.value, 10),
                }))
              }}
            />   */}
            <br />        

            <div className="formik__field__container">
                <label htmlFor="note">Note:</label>
                <textarea
                  required
                  id="note"
                  name="note"
                  onChange={(e:any) => {
                    console.log(e)
                    setInitialValues((prev) => ({
                      ...prev,
                      note: e.target.value,
                    }))
                    // Auto-expand textarea
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  className="formik__input"
                  value={initialValues.note}
                  placeholder="Type your note..."
                  style={{ minHeight: '40px', resize: 'vertical', overflow: 'hidden' }}
                  rows={2}
                />
            </div>

            <FormDropDownList
              label={'Priority:'}
              name={'notePriorityId'}
              data={priorityData}
              defaultText={priorityData.find((p) => p.value === initialValues.notePriorityId)?.text ?? 'Normal'}
              defaultValue={initialValues.notePriorityId}
              onChange={(e: any) => {
                setInitialValues((prev) => ({
                  ...prev,
                  notePriorityId: parseInt(e.value.value, 10),
                }))
              }}
            />

            <FormDatePicker 
              label={'Due Date (Optional):'}
              name={'dueDate'}
              value={initialValues.dueDate}
              onChange={(e: any) => {
                setInitialValues((prev) => ({
                  ...prev,
                  dueDate: e.value,
                }))
              }}
            />

            <button type="submit" className="submitBtn">Add Note</button>
          </form>
        </div>
    );
}

export default Notes;
